import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import log from '@/lib/logger';
import { useAppDispatch, useAppSelector } from 'features';
import {
  clearDownloadList,
  selectDownloadList,
  selectIsGeneratingFile,
  selectTaskIds,
  setIsGeneratingFile,
  setTaskId,
  setWebsocketFile,
  updateTaskId,
} from 'features/Downloads';
import { useFileDownloader } from 'hooks/useFileDownloader';
import useOnboarding from 'hooks/useOnboarding';
import { UserSessionContext } from 'providers/UserSessionProvider';
import { getTaskStatus } from 'services/metadata';
import { createFileDownloadFromUrl } from 'util/generateFile';

import DownloaderHeader from './DownloaderHeader';
import DownloaderList from './DownloaderList';

const DynamicDownload = () => {
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(true);
  const { synclogWebsocket } = useContext(UserSessionContext);
  const firstRender = useRef(true);
  const downloadList = useAppSelector(selectDownloadList);
  const isGeneratingFile = useAppSelector(selectIsGeneratingFile);
  const taskIds = useAppSelector(selectTaskIds);

  useOnboarding('', { dispatch }, () => dispatch(clearDownloadList()), 800);
  const { downloadFile } = useFileDownloader();

  const intervalsByTaskId = useRef<Record<string, NodeJS.Timeout>>({});

  const checkTaskStatus = useCallback(
    async (taskId: string) => {
      try {
        const response = await getTaskStatus(taskId);
        const newStatus = response.data.task_status;
        const fileUrl = response.data?.task_result;

        if (taskIds[taskId] === 'SUCCESS') {
          return;
        }

        if (newStatus === 'SUCCESS') {
          if (fileUrl) {
            const fileToDownload = createFileDownloadFromUrl(taskId, fileUrl);
            dispatch(setWebsocketFile(taskId, fileUrl));
            void downloadFile(fileToDownload);
          } else {
            dispatch(setWebsocketFile(taskId, 'FAILURE'));
          }
          dispatch(updateTaskId(taskId, newStatus));
          dispatch(setIsGeneratingFile(false));
        } else if (newStatus === 'FAILURE') {
          dispatch(updateTaskId(taskId, newStatus));
          dispatch(setIsGeneratingFile(false));
        } else {
          dispatch(setTaskId(taskId, newStatus));
        }
      } catch (error) {
        log.error(`[ERROR] Al consultar estado de tarea ${taskId}:`, error);
        return error;
      }
    },
    [dispatch, downloadFile, taskIds]
  );

  useEffect(() => {
    const activeTaskIds: string[] = [];
    const inactiveTaskIds: string[] = [];

    Object.entries(taskIds).forEach(([taskId, status]) => {
      if (status === 'SUCCESS' || status === 'FAILURE') {
        inactiveTaskIds.push(taskId);
      } else {
        activeTaskIds.push(taskId);
      }
    });

    activeTaskIds.forEach((taskId) => {
      if (!intervalsByTaskId.current[taskId]) {
        intervalsByTaskId.current[taskId] = setInterval(() => {
          void checkTaskStatus(taskId);
        }, 5000);
      }
    });

    inactiveTaskIds.forEach((taskId) => {
      if (intervalsByTaskId.current[taskId]) {
        clearInterval(intervalsByTaskId.current[taskId]);
        delete intervalsByTaskId.current[taskId];
      }
    });

    return () => {
      Object.values(intervalsByTaskId.current).forEach(clearInterval);
      intervalsByTaskId.current = {};
    };
  }, [taskIds, checkTaskStatus]);

  useEffect(() => {
    if (synclogWebsocket) {
      const newWebsocket = synclogWebsocket;

      if (newWebsocket.readyState === 1 && firstRender.current) {
        newWebsocket.send(
          JSON.stringify({
            type: 'subscribe',
            topic: 'synclog',
          })
        );
        firstRender.current = false;
      }

      newWebsocket.onmessage = (event) => {
        const json = JSON.parse(event?.data || '{}');

        if (json?.type === 'authorization' && json.message === false) {
          newWebsocket.close();
          return;
        }

        const isReportChannel = [
          '/chargepoints/transaction/report/',
          '/chargepoints/status_notification/report/',
          '/telemetry/tracker_path/report/',
          '/chargepoints/sampled_value/report/',
          '/chargepoints/monthly_connector_summary/report/',
          '/kushki/payment/report/',
        ].some((path) => !!json?.message?.includes?.(path));

        const isUpdateOrGenerated =
          !!json?.message?.includes?.('update') || !!json?.message?.includes?.('generated');

        if (isReportChannel && isUpdateOrGenerated) {
          const messageData = JSON.parse(json.message);
          const wsFileUrl = messageData?.data?.url;
          const id = messageData?.pk;

          if (!Object.keys(taskIds).includes(id) || taskIds[id] === 'SUCCESS') return;

          const fileToDownload = createFileDownloadFromUrl(id, wsFileUrl);
          dispatch(setWebsocketFile(id, wsFileUrl));
          void downloadFile(fileToDownload);
          dispatch(setIsGeneratingFile(false));
          dispatch(setTaskId(id, 'SUCCESS'));
        }
      };
    }
    return () => {
      if (synclogWebsocket) {
        synclogWebsocket.onmessage = () => {};
      }
    };
  }, [dispatch, downloadFile, synclogWebsocket, taskIds]);

  useEffect(() => {
    if (downloadList?.length || isGeneratingFile) setIsOpen(true);
  }, [downloadList?.length, isGeneratingFile]);

  return (
    <section
      className={`dynamic-download ${
        isGeneratingFile || Object.keys(taskIds)?.length > 0 || downloadList?.length
          ? 'enabled'
          : ''
      }`}
    >
      <DownloaderHeader isOpen={isOpen} toggleOpen={() => setIsOpen((prev) => !prev)} />
      {isOpen && <DownloaderList />}
    </section>
  );
};
export default DynamicDownload;
